import type { ComputedRef, Ref } from 'vue'
import { cloneDeep } from 'lodash-es'

import type { Call, CallPresentationHistory, ContentRating } from '@lib/types/call'
import { CallState } from '@lib/types/call'
import { CDP_EVENT_TYPE } from '@lib/constants/cdp.const'
import { computed, inject } from 'vue'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types'
import type { InstanceSettings } from '@lib/types/instanceSettings.types'

const DEFAULT_EVENT_DURATION = 3

const getEventType = (eventName: string) => {
  switch (eventName) {
    case CDP_EVENT_TYPE.fileExited:
    case CDP_EVENT_TYPE.canvasComponentExited:
    case CDP_EVENT_TYPE.canvasExited:
    case CDP_EVENT_TYPE.pageExited:
    case CDP_EVENT_TYPE.canvasPageExited:
      return 'exit'
    case CDP_EVENT_TYPE.fileEntered:
    case CDP_EVENT_TYPE.canvasComponentEntered:
    case CDP_EVENT_TYPE.canvasEntered:
    case CDP_EVENT_TYPE.pageEntered:
    case CDP_EVENT_TYPE.canvasPageEntered:
      return 'enter'
    default:
      return undefined
  }
}

const setMetadataItem = (call: Ref<Call>, id: string, rating: ContentRating) => {
  if (!call.value.presentationHistoryMetadata) {
    call.value.presentationHistoryMetadata = {}
  }

  call.value.presentationHistoryMetadata[id] = { rating }
}

const fileDuration = new Map<string, number>()
const openedPageTimestamps = new Map<string, number>()

export default function usePresentationHistory() {
  const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
    'pitcherSettings',
    computed(() => ({})),
  )

  const getMinimumEventDuration = () => {
    const eventDuration = pitcherSettings.value?.minimum_event_duration_in_call

    if (eventDuration && isFinite(eventDuration)) return eventDuration

    return DEFAULT_EVENT_DURATION
  }

  const updateCallPresentationHistory = (
    call: Ref<Call>,
    item: CallPresentationHistory[number],
    eventType: 'enter' | 'exit',
  ) => {
    const { presentationHistory = [] } = call.value

    if (presentationHistory.length > 1000) {
      console.warn('Presentation history is too long, skipping updates.')
      return
    }

    const updatedHistory = cloneDeep(presentationHistory)

    if (eventType === 'enter') {
      if (item.type === 'page') {
        openedPageTimestamps.set(item.id, new Date().getTime())
      }

      addPresentationHistoryItem(updatedHistory, item)
    } else if (eventType === 'exit') {
      if (item.type === 'page' && openedPageTimestamps.has(item.id)) {
        const startTime = openedPageTimestamps.get(item.id)
        const fileId = item.id.split('/')?.[0]

        if (startTime && fileId) {
          const duration = Math.round((new Date().getTime() - startTime) / 1000)
          fileDuration.set(fileId, duration + (fileDuration.get(fileId) ?? 0))
        }
        openedPageTimestamps.delete(item.id)
      }

      updateOrDeletePresentationHistoryItem(updatedHistory, item)
    }

    call.value = { ...call.value, presentationHistory: updatedHistory }
  }

  const addPresentationHistoryItem = (
    updatedHistory: CallPresentationHistory,
    item: CallPresentationHistory[number],
  ) => {
    if (!['file', 'canvas', 'section'].includes(item.type)) {
      return
    }
    // Check if an item with the same ID already exists and has a duration (completed)
    const existingIndex = updatedHistory.findIndex(
      (historyItem) => historyItem.id === item.id && historyItem.duration !== undefined,
    )

    if (existingIndex !== -1) {
      // Store the accumulated duration temporarily
      updatedHistory[existingIndex] = {
        ...updatedHistory[existingIndex],
        accumulatedDuration: updatedHistory[existingIndex].duration,
        lastTimeStarted: new Date().toISOString(),
      }
    } else {
      // Add new item
      updatedHistory.push({ ...item, timeStarted: new Date().toISOString() })
    }
  }

  const updateOrDeletePresentationHistoryItem = (
    updatedHistory: CallPresentationHistory,
    item: CallPresentationHistory[number],
  ) => {
    if (!['file', 'canvas', 'section'].includes(item.type)) {
      return
    }

    const index = findMatchingHistoryItemIndex(updatedHistory, item)
    if (index === -1) return

    const lastTimeStarted = updatedHistory[index].lastTimeStarted ?? updatedHistory[index].timeStarted
    let { duration, shouldDeleteItem } = calculateEventDuration(lastTimeStarted)

    if (item.type === 'file' && fileDuration.has(item.id)) {
      duration = fileDuration.get(item.id) ?? 0
      shouldDeleteItem = getMinimumEventDuration() > duration
      fileDuration.delete(item.id)
    }

    // Check if we have an accumulated duration from previous visits
    const accumulatedDuration = updatedHistory[index].accumulatedDuration || 0
    const totalDuration = (duration ?? 0) + accumulatedDuration

    if (shouldDeleteItem && accumulatedDuration === 0) {
      console.warn(
        `The event "${updatedHistory[index].type}" was skipped during the call, as it did not reach the required duration limit.`,
      )
      updatedHistory.splice(index, 1)
    } else {
      updatedHistory[index] = {
        ...updatedHistory[index],
        ...item,
        duration: totalDuration,
      }
      delete updatedHistory[index].lastTimeStarted
      delete updatedHistory[index].accumulatedDuration
    }
  }

  const findMatchingHistoryItemIndex = (history: CallPresentationHistory, item: CallPresentationHistory[number]) =>
    history.findIndex((historyItem) => historyItem.id === item.id && historyItem.type === item.type)

  const calculateEventDuration = (startedAt?: string) => {
    if (!startedAt) return { duration: undefined, shouldDeleteItem: false }

    const timeStarted = new Date(startedAt).getTime()
    const timeEnded = Date.now()
    const duration = Math.round((timeEnded - timeStarted) / 1000)

    const minimumEventDuration = getMinimumEventDuration()
    const shouldDeleteItem = duration < minimumEventDuration

    return { duration, shouldDeleteItem }
  }

  const updatePresentationHistory = (p: {
    eventName: (typeof CDP_EVENT_TYPE)[keyof typeof CDP_EVENT_TYPE]
    payload: any
    call: Ref<Call>
    canvasName?: string
  }) => {
    const { eventName, payload, call, canvasName = '' } = p

    // We should update presentation history when call stopped to be able to track exit events called on beforeOnmount
    if (call.value.callState === CallState.NO_CALL) {
      return
    }

    const CANVAS_EVENTS = [
      CDP_EVENT_TYPE.canvasEntered,
      CDP_EVENT_TYPE.canvasExited,
    ] as (typeof CDP_EVENT_TYPE)[keyof typeof CDP_EVENT_TYPE][]
    const CANVAS_PAGE_EVENTS = [
      CDP_EVENT_TYPE.canvasPageEntered,
      CDP_EVENT_TYPE.canvasPageExited,
    ] as (typeof CDP_EVENT_TYPE)[keyof typeof CDP_EVENT_TYPE][]
    const COMPONENT_EVENTS = [
      CDP_EVENT_TYPE.canvasComponentEntered,
      CDP_EVENT_TYPE.canvasComponentExited,
    ] as (typeof CDP_EVENT_TYPE)[keyof typeof CDP_EVENT_TYPE][]
    const FILE_EVENTS = [
      CDP_EVENT_TYPE.fileEntered,
      CDP_EVENT_TYPE.fileExited,
    ] as (typeof CDP_EVENT_TYPE)[keyof typeof CDP_EVENT_TYPE][]
    const PAGE_EVENTS = [
      CDP_EVENT_TYPE.pageEntered,
      CDP_EVENT_TYPE.pageExited,
    ] as (typeof CDP_EVENT_TYPE)[keyof typeof CDP_EVENT_TYPE][]

    const isCanvasEnterExited = CANVAS_EVENTS.includes(eventName)
    const isFileEnterExited = FILE_EVENTS.includes(eventName)
    const isPageEnterExited = PAGE_EVENTS.includes(eventName)
    const isCanvasPageEnterExited = CANVAS_PAGE_EVENTS.includes(eventName)
    const isSectionEnterExited = COMPONENT_EVENTS.includes(eventName) && payload?.component_type === 'Section'
    const eventType = getEventType(eventName)
    const isEnter = eventType === 'enter'

    if (!eventType) {
      console.warn(`[Presentation History] Event type: "${eventType}" is not supported.`)
      return
    }

    if (isCanvasEnterExited) {
      updateCallPresentationHistory(
        call,
        {
          type: 'canvas',
          id: payload?.canvas_id,
          name: canvasName,
        },
        eventType,
      )
    } else if (isSectionEnterExited) {
      updateCallPresentationHistory(
        call,
        {
          type: 'section',
          id: payload?.component_id,
          name: payload?.component_name,
        },
        eventType,
      )
    } else if (isCanvasPageEnterExited) {
      updateCallPresentationHistory(
        call,
        {
          type: 'canvas_page',
          id: `${payload?.canvas_id}/${payload?.page}`,
          // Exclude component_ids due to potential performance issues with large arrays in localStorage
          // component_ids: payload?.component_ids,
          name: payload?.name,
        },
        eventType,
      )
    } else if (isFileEnterExited) {
      updateCallPresentationHistory(
        call,
        isEnter
          ? {
              type: 'file',
              id: payload?.file_id,
              name: payload?.file_name,
            }
          : {
              type: 'file',
              id: payload?.file_id,
            },
        eventType,
      )
    } else if (isPageEnterExited) {
      let page = parseInt(payload?.url?.split('/')?.pop())
      const type = 'page'

      if (isNaN(page)) {
        page = 0
      }

      const id = payload?.file_id + '/' + page

      updateCallPresentationHistory(
        call,
        isEnter
          ? {
              type,
              id,
              name: payload?.file_name,
              page,
            }
          : {
              type,
              id,
              page,
            },
        eventType,
      )
    }
  }

  return {
    updatePresentationHistory,
    setMetadataItem,
  }
}
